import React from 'react';

import Typography from '@mui/material/Typography';
import { Theme } from '@mui/material/styles';

import styled from '../../../../styled';
import {
  colorsPalette,
  LARGE_MARGIN,
  LARGE_MARGIN_PX,
  MIDDLE_MARGIN,
  SMALL_MARGIN,
} from '../../../../theme';
import { Background } from '../../../../types';
import { LazyLoadImage } from '../../../LazyloadImage';
import useStyles from './styles';

interface SlideProps {
  imagePath: string;
  listItems: string[];
  title: string;
  slideColor: Background;
}

const Slide = ({ imagePath, listItems = [], title, slideColor }: SlideProps) => {
  const isDark = slideColor === Background.Dark;
  const classes = useStyles(isDark)();
  return (
    <Wrapper data-testid="tabsSliderBlockSlide">
      <Shadow isDark={isDark}>
        <LazyLoadImage
          data-testid="slide-img"
          imageWrapperClass={classes.wrapper}
          path={imagePath}
        />
        <SlideContent>
          <Title data-testid="slide-title">{title}</Title>
          <List>
            {listItems.map((item, index) => (
              <ListItem key={index} data-testid="slide-list-item">
                {item}
              </ListItem>
            ))}
          </List>
        </SlideContent>
      </Shadow>
    </Wrapper>
  );
};

export default Slide;

const Title = styled(Typography)(({ theme }: { theme: Theme }) => ({
  marginBottom: 24,
  fontWeight: 700,
  fontSize: 18,
  [theme.breakpoints.up('md')]: {
    fontSize: 20,
  },
}));
const Wrapper = styled('div')({
  maxWidth: '100%',
  height: '100%',
});
const Shadow = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isDark',
})`
  height: 100%;
  margin: 3px 3px 5px;
  border-radius: 8px;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.4);
  background: ${colorsPalette.blackSecondary};
  ${({ theme }) => theme.breakpoints.up('sm')} {
    background: ${({ isDark }) => (isDark ? colorsPalette.blueDark : colorsPalette.blackSecondary)};
  }
  ${({ theme }) => theme.breakpoints.up('md')} {
    display: flex;
    flex-direction: row-reverse;
    min-height: 500px;
  }
`;
const SlideContent = styled('div')(({ theme }: { theme: Theme }) => ({
  position: 'relative',
  width: '100%',
  boxSizing: 'border-box',
  padding: `16px 16px ${MIDDLE_MARGIN}px`,
  [theme.breakpoints.up('sm')]: {
    padding: `24px 24px ${MIDDLE_MARGIN}px`,
  },
  [theme.breakpoints.up('md')]: {
    position: 'relative',
    width: '50%',
    padding: `${LARGE_MARGIN}px 0 ${LARGE_MARGIN_PX} ${LARGE_MARGIN}px`,
  },
}));
const List = styled('ul')({
  padding: 0,
  listStyleType: 'none',
  marginBottom: 0,
});
const ListItem = styled('li')(({ theme }: { theme: Theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: SMALL_MARGIN,
  color: colorsPalette.gray,
  fontSize: 16,
  '&:last-child': {
    marginBottom: 0,
  },
  [theme.breakpoints.up('md')]: {
    alignItems: 'center',
    marginRight: MIDDLE_MARGIN,
    fontSize: 18,
  },
  '&:before': {
    content: '""',
    display: 'block',
    height: 8,
    width: 8,
    minWidth: 8,
    marginTop: 8,
    marginRight: 8,
    borderRadius: '50%',
    background: colorsPalette.blue,
    [theme.breakpoints.up('md')]: {
      marginTop: 0,
      marginRight: SMALL_MARGIN,
    },
  },
}));
